import definitions from '../../../../config/static_data/game_versions.json'
import type Difficulty from './Difficulty'

export type GameVersionCapability =
  | 'blizzard_api'
  | 'manual_characters'
  | 'audit'
  | 'audit_tiles'
  | 'loot'
  | 'raid_plans'
  | 'selection_image'
  | 'housing'
  | 'mythic_plus'
  | 'great_vault'
  | 'item_upgrades'
  | 'crests'
  | 'professions'
  | 'achievements'
  | 'raid_progress'
  | 'raid_progress_tile'
  | 'guild_message_tile'
  | 'difficulties'
  | 'activity_timeline'
  | 'upgrade_tracks'
  | 'retail_gear_audit'
  | 'gear_quality_audit'
  | 'bonus_rolls'
  | 'honor_level'
  | 'defensive_insights'
  | 'retail_collections'
  | 'flexible_raid_size'
  | 'split_raid_size'
  | 'ranged_slot'
  | 'instance_activities_only'
  | 'realm_rulesets'
  | 'guild_candidates'
  | 'linked_guilds_onboarding'

export type GameVersionConfig = {
  subdomain: string
  logo: string
  header_image: string
  redis_prefix: string
  max_level: number
  default_difficulty: Difficulty
  default_audit_category: 'summary' | 'gear_overview'
  wcl_subdomain: string
  wcl_difficulties: string[]
  wowhead_prefix: string
  character_media: 'main-raw' | 'avatar'
  realm_search_min_length: number
  require_buffs_options: 'full' | 'classic'
  tier_set: { pieces: number; bonus_threshold: number } | null
  tier_tracking: 'per_season' | 'per_tier'
  enchant_field_format: 'retail' | 'classic'
  special_gem: 'epic_gem' | 'meta_gem' | null
  gem_quality_levels: number
  mounts_field: 'mounts' | 'mounts_obtained'
}

type GameVersion = keyof typeof definitions

type GameVersionDefinition = {
  capabilities: GameVersionCapability[]
  config: GameVersionConfig
}

export const gameVersionDefinitions = definitions as Record<
  GameVersion,
  GameVersionDefinition
>

export const gameVersions = Object.keys(definitions) as GameVersion[]

export const gameVersionHas = (
  capability: GameVersionCapability,
  version: GameVersion = window.page_info.gameVersion,
) => gameVersionDefinitions[version].capabilities.includes(capability)

export const gameVersionConfig = (
  version: GameVersion = window.page_info.gameVersion,
) => gameVersionDefinitions[version].config

export const gameVersionToSubdomain = Object.fromEntries(
  gameVersions.map((version) => [
    version,
    gameVersionConfig(version).subdomain,
  ]),
) as Record<GameVersion, string>

export const gameVersionHref = (version: GameVersion) => {
  const protocol = location.host.includes('local') ? 'http' : 'https'
  const prefixes = [
    'www.',
    'live.',
    ...gameVersions.map((other) => gameVersionConfig(other).subdomain),
  ].filter(Boolean)
  let baseHost = location.host
  let prefix: string | undefined
  while ((prefix = prefixes.find((p) => baseHost.startsWith(p)))) {
    baseHost = baseHost.slice(prefix.length)
  }

  return `${protocol}://${gameVersionConfig(version).subdomain}${baseHost}`
}

export const gameVersionLogo = (version: GameVersion) =>
  `${window.page_info.assetHost}/images/game-versions/${
    gameVersionConfig(version).logo
  }-logo.avif`

export default GameVersion
